<div class="page-container" [class.dialog-mode]="isDialogMode">
  <!-- Main Content -->
  <main class="page-main">
    <!-- Page Content -->
    <div class="page-content">
      <!-- Header Section -->
      <div class="page-title-container" [class.hidden]="isDialogMode">
        <app-breadcrumb></app-breadcrumb>
        <h1 class="page-title">Resource Details</h1>
        @if (resource && !isLoading && !error) {
          <div class="page-title-actions">
            @if (isComparisonPopUpsEnabled) {
              <div class="title-action" (click)="openInFloatingDialog()">
                <mat-icon class="title-action-icon">select_window_2</mat-icon>
                <span class="title-action-label">Open in dialog</span>
              </div>
            }
            @if (isTimeOffTrackingEnabled) {
              <div class="title-action" (click)="scrollToTimeOff()">
                <mat-icon class="title-action-icon">calendar_clock</mat-icon>
                <span class="title-action-label">Time Off</span>
              </div>
            }
          </div>
        }
      </div>
      <!-- Loading State -->
      @if (isLoading) {
        <mat-card class="resource-details-card">
          <div class="loading-state">
            <mat-spinner diameter="32"></mat-spinner>
            <p>Loading resource details...</p>
          </div>
        </mat-card>
      }

      <!-- Error State -->
      @if (error && !isLoading) {
        <mat-card class="resource-details-card">
          <div class="error-state">
            <mat-icon class="error-icon">lock</mat-icon>
            <h3>Resource unavailable</h3>
            <p>{{ error }}</p>
            <button mat-raised-button class="go-back-button" (click)="goBack()">
              <mat-icon>arrow_back</mat-icon>
              Go Back
            </button>
          </div>
        </mat-card>
      }

      <!-- Resource Profile Card -->
      @if (resource && !isLoading && !error) {
        <!-- Profile + Skills row (50/50), matching the User Profile layout -->
        <div class="profile-attributes-row">
          <mat-card class="resource-details-card resource-profile-card">
            <div class="resource-details">
              <!-- Resource Header -->
              <div class="resource-header">
                <div class="resource-profile">
                  <div class="avatar-container">
                    <div
                      class="resource-avatar initials-avatar"
                      [title]="resource.fullName"
                    >
                      {{ getInitials(resource.fullName) }}
                    </div>
                  </div>
                  <div class="resource-info">
                    <div class="name-and-id">
                      <h1 class="resource-name">{{ resource.fullName }}</h1>
                      <app-copyable-text
                        [value]="resource.employeeId"
                        [displayText]="'ID: ' + resource.employeeId"
                        copyTitle="Click to copy employee ID"
                        [uppercase]="true"
                      />
                      @if (
                        resource.practiceAreas &&
                        resource.practiceAreas.length > 0
                      ) {
                        <app-membership-chips
                          [items]="resource.practiceAreas"
                          labelKey="description"
                          [maxVisible]="99"
                        />
                      }
                    </div>

                    <div class="resource-details-row">
                      <div class="detail-group">
                        <mat-icon class="detail-icon">work</mat-icon>
                        <span class="detail-text">{{
                          resource.title || "Not provided"
                        }}</span>
                      </div>
                    </div>

                    <div class="resource-details-row">
                      <div class="detail-group">
                        <mat-icon class="detail-icon">school</mat-icon>
                        <span class="detail-text">
                          Class
                          @if (resource.classYear) {
                            of {{ resource.classYear
                            }}{{
                              resource.seniority
                                ? " - " + resource.seniority
                                : ""
                            }}
                          } @else {
                            Not provided
                          }
                        </span>
                      </div>
                    </div>

                    <div class="resource-details-row">
                      <div class="detail-group">
                        <mat-icon class="detail-icon">location_on</mat-icon>
                        <span class="detail-text">{{
                          resource.office || "Not provided"
                        }}</span>
                      </div>
                    </div>

                    <div class="resource-details-row">
                      <div class="detail-group">
                        <mat-icon class="detail-icon">phone</mat-icon>
                        <span class="detail-text">{{
                          resource.phoneNumber || "Not provided"
                        }}</span>
                      </div>
                    </div>

                    <div class="resource-details-row">
                      <div class="detail-group">
                        <mat-icon class="detail-icon">email</mat-icon>
                        @if (resource.email) {
                          <a
                            [href]="'mailto:' + resource.email"
                            class="detail-text email-link"
                            >{{ resource.email }}</a
                          >
                        } @else {
                          <span class="detail-text">Not provided</span>
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </mat-card>

          <!-- Skills & Attributes Card (right 50%, beside the profile card).
             In dialog mode it moves to the very bottom instead, so Billing
             History is the first card visible after the profile. -->
          @if (!isDialogMode) {
            <mat-card class="resource-details-card attributes-card">
              <app-resource-attributes
                [employeeId]="resource.employeeId"
                [isReadOnly]="isAttributesReadOnly"
              ></app-resource-attributes>
            </mat-card>
          }
        </div>

        <!-- Billing History and Billing Forecast Row (50/50 layout) -->
        <div class="billing-summary-row">
          <!-- Billing History Card -->
          <mat-card class="billing-history-card">
            <app-resource-billing-history
              [employeeId]="resource.employeeId"
              [breadcrumbContext]="getResourceBreadcrumbContext()"
            ></app-resource-billing-history>
          </mat-card>

          <!-- Billing Forecast Card -->
          <mat-card class="billing-summary-card">
            <app-resource-billing-summary
              [employeeId]="resource.employeeId"
            ></app-resource-billing-summary>
          </mat-card>
        </div>

        <!--
          Capacity Management | Time Off (50/50). Without time-off tracking,
          Capacity takes the row alone at full width.
        -->
        <div class="capacity-time-off-row">
          <!-- Capacity Management Section -->
          <div class="capacity-grid">
            <app-capacity-management
              [employeeId]="resource.employeeId"
              [isReadOnly]="isCapacityReadOnly"
              [canEditCapacitySettings]="!isCapacityReadOnly"
              (editModeRequested)="onCapacityEditModeRequested()"
            ></app-capacity-management>
          </div>

          @if (isTimeOffTrackingEnabled) {
            <mat-card
              class="resource-details-card time-off-card"
              #timeOffSection
            >
              <app-time-off-management
                [entries]="timeOffEntries"
                [lookupTypes]="timeOffTypeLookups"
                [isReadOnly]="isTimeOffReadOnly"
                [canEditEntitledDays]="canEditEntitledDays"
                [canApprove]="canApproveTimeOff"
                [apiMode]="'admin'"
                [employeeId]="employeeId!"
                [employeeName]="resource ? resource.fullName : ''"
                [classYear]="resource.classYear"
                [seniority]="resource.seniority"
                (entriesSaved)="onTimeOffSaved()"
                (entitledDaysSaved)="onEntitledDaysSaved()"
                (editModeRequested)="onTimeOffEditModeRequested()"
                (requestStatusChanged)="onTimeOffSaved()"
              ></app-time-off-management>
            </mat-card>
          }
        </div>

        <!--
          Pending Assignments Card

          showViewAllNotes is true because this page renders the Assignment
          Notes card that the action scrolls to, not because of a permission:
          the card offers the action whenever the host does, and which notes
          the Assignment Notes card then shows is decided by the API — the
          viewer's own note needs no permission at all.
        -->
        <mat-card class="resource-details-card pending-assignments-card">
          <app-assignments-card
            mode="resource"
            [assignments]="assignmentRows"
            [filterResetKey]="employeeId"
            [showViewAllNotes]="true"
            (viewAllNotesClicked)="scrollToAssignmentNotes()"
            [isLoading]="isLoadingAssignments"
            [canEdit]="canEditAssignments"
            [canCreate]="canCreateAssignments"
            [canRemove]="canDeleteAssignments"
            [canReadResourceNotes]="canReadResourceAssignmentNotes"
            [showManagerNotes]="canReadManagerAssignmentNotes"
            [canWriteManagerNotes]="canWriteManagerAssignmentNotes"
            (matterClicked)="navigateToMatter($event)"
            (removeClicked)="onRemoveAssignment($event)"
            (assignmentRemoved)="loadAssignmentsAfterRemove($event)"
            (assignmentChanged)="onAssignmentChanged($event)"
            [showAddAssignment]="canCreateAssignments"
            [addAssignmentDisabled]="isAddingAssignment"
            [showFilter]="!isAddingAssignment"
            (addAssignmentClicked)="startAddAssignment()"
          >
            @if (canCreateAssignments && isAddingAssignment) {
              <!-- Escape is bound here rather than on the typeahead input, which
                   is replaced once a matter is staged — the point at which there
                   is most to discard. keydown bubbles from whichever field has
                   focus; an open datepicker calendar lives in a CDK overlay
                   outside this subtree, so its own Escape is unaffected. -->
              <div
                class="add-assignment-form-container"
                (keydown.escape)="cancelAddAssignment()"
              >
                <!-- Column widths come from .add-assignment-form-table in
                     _add-assignment-typeahead.scss, shared with matter details. -->
                <table class="add-assignment-form-table">
                  <thead>
                    <tr>
                      <th>Client Matter</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr class="inline-add-row">
                      <td class="inline-add-cell assignment-name-cell">
                        @if (stagedMatter) {
                          <div class="staged-matter-label">
                            <span>{{
                              stagedMatter.matterName ||
                                stagedMatter.clientMatterNumber
                            }}</span>
                            <span class="staged-matter-number">{{
                              stagedMatter.clientMatterNumber
                            }}</span>
                          </div>
                        } @else {
                          <div class="typeahead-wrapper">
                            <mat-icon class="typeahead-search-icon"
                              >search</mat-icon
                            >
                            <input
                              #addAssignmentInput
                              type="text"
                              class="typeahead-input"
                              placeholder="Add a matter by name..."
                              [value]="addMatterQuery"
                              (input)="onAddMatterInput($event)"
                              (blur)="onAddMatterBlur()"
                              [disabled]="isCreatingAssignment"
                              autocomplete="off"
                            />
                            @if (addMatterQuery) {
                              <mat-icon
                                class="typeahead-suffix-icon"
                                (click)="clearAddMatterTypeahead()"
                                >close</mat-icon
                              >
                            }
                          </div>
                          @if (isSearchingMatters) {
                            <div class="typeahead-status">
                              <mat-spinner diameter="16"></mat-spinner>
                              <span>Searching...</span>
                            </div>
                          } @else if (matterSuggestions.length > 0) {
                            <div
                              class="typeahead-suggestions"
                              role="listbox"
                              aria-label="Matter suggestions"
                            >
                              @for (
                                m of matterSuggestions;
                                track m.clientMatterNumber
                              ) {
                                <div
                                  role="option"
                                  class="typeahead-suggestion"
                                  (mousedown)="onSelectMatterToAssign(m)"
                                >
                                  <span class="suggestion-name">{{
                                    m.matterName
                                  }}</span>
                                  <span class="suggestion-meta">
                                    {{ m.clientMatterNumber }}
                                  </span>
                                </div>
                              }
                            </div>
                          } @else if (
                            addMatterQuery.trim().length >= 3 &&
                            matterNotFound
                          ) {
                            <div class="typeahead-status">
                              <span>No matching matters found.</span>
                            </div>
                          }
                        }
                      </td>
                      <td class="inline-add-cell">
                        <mat-form-field
                          appearance="outline"
                          class="inline-add-field"
                        >
                          <input
                            matInput
                            [matDatepicker]="startPicker"
                            [(ngModel)]="stagedStartDate"
                            placeholder="mm/dd/yyyy"
                            [disabled]="!stagedMatter || isCreatingAssignment"
                          />
                          <mat-datepicker-toggle
                            matSuffix
                            [for]="startPicker"
                            [disabled]="!stagedMatter || isCreatingAssignment"
                          ></mat-datepicker-toggle>
                          <mat-datepicker #startPicker></mat-datepicker>
                        </mat-form-field>
                      </td>
                      <td class="inline-add-cell">
                        <mat-form-field
                          appearance="outline"
                          class="inline-add-field"
                        >
                          <input
                            matInput
                            [matDatepicker]="endPicker"
                            [(ngModel)]="stagedEndDate"
                            [min]="stagedStartDate"
                            placeholder="mm/dd/yyyy"
                            [disabled]="!stagedMatter || isCreatingAssignment"
                          />
                          <mat-datepicker-toggle
                            matSuffix
                            [for]="endPicker"
                            [disabled]="!stagedMatter || isCreatingAssignment"
                          ></mat-datepicker-toggle>
                          <mat-datepicker #endPicker></mat-datepicker>
                        </mat-form-field>
                      </td>
                      <td class="inline-add-actions-cell">
                        @if (isCreatingAssignment) {
                          <mat-spinner diameter="18"></mat-spinner>
                        } @else {
                          <div class="inline-add-actions">
                            <button
                              mat-icon-button
                              class="inline-save-btn"
                              matTooltip="Assign"
                              matTooltipClass="light-tooltip"
                              type="button"
                              [disabled]="!stagedMatter"
                              (click)="onConfirmAssignment()"
                            >
                              <mat-icon>check</mat-icon>
                            </button>
                            <button
                              mat-icon-button
                              class="inline-cancel-btn"
                              matTooltip="Cancel"
                              matTooltipClass="light-tooltip"
                              type="button"
                              (click)="cancelAddAssignment()"
                            >
                              <mat-icon>close</mat-icon>
                            </button>
                          </div>
                        }
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            }
          </app-assignments-card>
        </mat-card>

        <!-- Assignment Notes | Resource Notes (50/50) -->
        <div class="notes-row">
          <!--
            Every assignment note for this resource, shown by default. It is fed
            the list the page already holds, so it costs no second request; while
            that list loads it gets null, so the card shows its loading state
            rather than briefly reading as empty, and a failed load shows the
            card's error rather than "no notes".
          -->
          <mat-card
            class="resource-details-card assignment-notes-card"
            tabindex="-1"
            role="region"
            aria-label="Assignment notes"
            #assignmentNotesSection
          >
            <app-assignment-notes-panel
              mode="resource"
              [employeeId]="resource.employeeId"
              [entityName]="resource.fullName"
              [assignments]="isLoadingAssignments ? null : pendingAssignments"
              [loadFailed]="assignmentsLoadFailed"
            />
          </mat-card>

          <mat-card class="resource-details-card notes-card">
            <app-entity-notes
              [entityId]="resource.employeeId"
              [notesInfo]="resource.notesInfo"
              [isReadOnly]="isNotesReadOnly"
              [breadcrumbContext]="getResourceBreadcrumbContext()"
              title="Resource Notes"
              placeholder="Click Edit to add notes about this resource..."
              (notesSaved)="onNotesSaved($event)"
              (editModeRequested)="onNotesEditModeRequested()"
            ></app-entity-notes>
          </mat-card>
        </div>

        <!-- Skills & Attributes Card at the bottom (dialog mode only), so
             Billing History stays visible right below the profile card. -->
        @if (isDialogMode) {
          <mat-card
            class="resource-details-card attributes-card attributes-card--bottom"
          >
            <app-resource-attributes
              [employeeId]="resource.employeeId"
              [isReadOnly]="isAttributesReadOnly"
            ></app-resource-attributes>
          </mat-card>
        }

        <!-- Back to top -->
        <div class="back-to-top-link" (click)="scrollToTop()">
          <span>Back to top</span>
        </div>
      }
    </div>
  </main>
</div>
